import { type FC } from "react";
import { Building2, ChevronRight } from "lucide-react";

import { formatCurrency } from "@/shared/utils/formatters";
import { getInstitutionLogo } from "@/shared/utils/institutionLogos";
import { CATEGORY_ICONS } from "@/shared/constants/estateCategories";
import { getCategoryStyle } from "@/shared/utils/categoryColors";

import type { EstateItem } from "@/shared/types/estateTypes";

interface LinkedMortgageSectionProps {
  debts: EstateItem[];
  propertyBalance: number;
  totalDebtBalance: number;
  onSelect?: (id: number) => void;
}

export const LinkedMortgageSection: FC<LinkedMortgageSectionProps> = ({
  debts,
  propertyBalance,
  totalDebtBalance,
  onSelect,
}) => {
  const label = debts[0]?.item_type || "Mortgage";

  return (
    <>
      <hr className="mx-6 border-primary" />
      <div className="px-6 py-6">
        <h4 className="mb-4">{label}</h4>
        <div className="flex flex-col items-start gap-4 self-stretch rounded-lg border border-primary bg-white p-4">
          {debts.map((debt, index) => {
            const hardcodedLogo = getInstitutionLogo(debt.item_name || "");
            const logoSrc =
              hardcodedLogo ||
              (debt.plaid_institution_logo
                ? `data:image/png;base64,${debt.plaid_institution_logo}`
                : null);
            const accountLast4 = debt.plaid_account_mask || debt.identifiers?.account_last4;
            const subtitle = [
              debt.item_type,
              accountLast4 ? `Account ending \u00b7\u00b7${accountLast4}` : null,
            ]
              .filter(Boolean)
              .join(" \u00b7 ");

            return (
              <div key={debt.id} className="w-full">
                {index > 0 && <hr className="mb-4 w-full border-primary" />}
                <button
                  type="button"
                  className="flex w-full items-center gap-3 transition-colors"
                  onClick={() => onSelect?.(debt.id)}
                >
                  {logoSrc ? (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
                      <img
                        src={logoSrc}
                        alt={`${debt.item_name || ""} logo`}
                        className="h-8 w-8 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-50">
                      <Building2 className="h-5 w-5 text-orange-500" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-medium">{debt.item_name}</p>
                    {subtitle && <p className="truncate text-2xs text-secondary">{subtitle}</p>}
                  </div>
                  <p className="shrink-0 text-sm font-medium">-{formatCurrency(debt.balance)}</p>
                </button>
              </div>
            );
          })}
          <hr className="w-full border-primary" />
          <div className="flex w-full items-center justify-between">
            <p className="text-sm text-secondary">Net to estate</p>
            <p className="text-sm font-medium">
              {formatCurrency(propertyBalance - totalDebtBalance)}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

interface LinkedPropertySectionProps {
  property: EstateItem;
  onSelect?: (id: number) => void;
}

export const LinkedPropertySection: FC<LinkedPropertySectionProps> = ({ property, onSelect }) => {
  const subtitle = [property.item_type, property.category].filter(Boolean).join(" \u00b7 ");
  const Icon = CATEGORY_ICONS[property.category as keyof typeof CATEGORY_ICONS];
  const style = getCategoryStyle(property.category);

  return (
    <>
      <hr className="mx-6 border-primary" />
      <div className="px-6 py-6">
        <h4 className="mb-4">Secured by</h4>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
          onClick={() => onSelect?.(property.id)}
        >
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.bgClass}`}
          >
            {Icon ? <Icon className={`h-5 w-5 ${style.textClass}`} /> : null}
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className="truncate text-sm font-medium">{property.item_name}</p>
            {subtitle && <p className="truncate text-2xs text-secondary">{subtitle}</p>}
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-tertiary" />
        </button>
      </div>
    </>
  );
};
